import { useEffect, useState } from "react";
import DashboardShell from "@/components/layout/DashboardShell";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { Search, PlayCircle, MessageCircle, CheckCircle2, CircleDot, Circle } from "lucide-react";
import HelpVideoPlayer, { videoThumb, WHATSAPP_HELP } from "@/components/HelpVideoPlayer";

const BADGE = {
  not_watched: { icon: Circle, cls: "bg-muted text-muted-foreground", key: "help.notWatched" },
  partial: { icon: CircleDot, cls: "bg-amber-100 text-amber-800", key: "help.partial" },
  completed: { icon: CheckCircle2, cls: "bg-emerald-100 text-emerald-800", key: "help.completed" },
};

function ProgressBadge({ status }) {
  const { t } = useT();
  const b = BADGE[status] || BADGE.not_watched;
  const Icon = b.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${b.cls}`}>
      <Icon className="w-3 h-3" /> {t(b.key)}
    </span>
  );
}

function VideoCard({ video, lang, onPlay }) {
  const title = lang === "en" ? (video.title_en || video.title_hi) : (video.title_hi || video.title_en);
  const thumb = videoThumb(video);
  return (
    <button
      onClick={() => onPlay(video)}
      data-testid={`help-video-card-${video.video_id}`}
      className="am-card text-left overflow-hidden group hover:shadow-md transition-shadow"
    >
      <div className="aspect-video bg-muted relative">
        {thumb ? (
          <img src={thumb} alt={title} loading="lazy" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full grid place-items-center text-muted-foreground"><PlayCircle className="w-10 h-10" /></div>
        )}
        <div className="absolute inset-0 grid place-items-center bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity">
          <PlayCircle className="w-12 h-12 text-white drop-shadow" />
        </div>
      </div>
      <div className="p-3">
        <div className="font-semibold text-sm leading-snug line-clamp-2">{title}</div>
        <div className="mt-2"><ProgressBadge status={video.progress} /></div>
      </div>
    </button>
  );
}

export default function HelpCenter() {
  const { t, lang } = useT();
  const [q, setQ] = useState("");
  const [cats, setCats] = useState(null);
  const [playing, setPlaying] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => {
      api.get("/help/center", { params: q ? { search: q } : {} })
        .then((r) => setCats(r.data.categories || []))
        .catch(() => setCats([]));
    }, q ? 300 : 0);
    return () => clearTimeout(id);
  }, [q]);

  const onProgress = (videoId, status) =>
    setCats((cs) => (cs || []).map((c) => ({
      ...c,
      videos: c.videos.map((v) => (v.video_id === videoId ? { ...v, progress: status } : v)),
    })));

  const catName = (c) => (lang === "en" ? (c.name_en || c.name_hi) : (c.name_hi || c.name_en));
  const total = (cats || []).reduce((n, c) => n + c.videos.length, 0);

  return (
    <DashboardShell
      title={t("help.title")}
      subtitle={t("help.subtitle")}
      action={
        <a href={WHATSAPP_HELP} target="_blank" rel="noreferrer">
          <Button className="am-btn-primary rounded-full" data-testid="help-need-help-btn">
            <MessageCircle className="w-4 h-4 mr-1.5" /> {t("help.needHelp")}
          </Button>
        </a>
      }
    >
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="help-search-input"
            className="am-input pl-11 py-5"
            placeholder={t("help.searchPlaceholder")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>

        {cats === null ? (
          <div className="text-sm text-muted-foreground py-10 text-center">{t("common.loading")}</div>
        ) : q && total === 0 ? (
          <div className="am-card p-8 text-center text-sm text-muted-foreground" data-testid="help-no-results">
            {t("help.noResults")}
          </div>
        ) : (
          cats.map((c) => (
            <section key={c.key} data-testid={`help-category-${c.key}`}>
              <h2 className="font-display font-bold text-lg mb-3">{catName(c)}</h2>
              {c.videos.length === 0 ? (
                <div className="am-card p-4 text-xs text-muted-foreground">{t("help.noVideos")}</div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {c.videos.map((v) => (
                    <VideoCard key={v.video_id} video={v} lang={lang} onPlay={(vid) => { setPlaying(vid); setOpen(true); }} />
                  ))}
                </div>
              )}
            </section>
          ))
        )}
      </div>
      {playing && <HelpVideoPlayer video={playing} open={open} onOpenChange={setOpen} onProgress={onProgress} />}
    </DashboardShell>
  );
}
